// Organized imports
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Async action to fetch a sub-quest by its ID
// Makes a GET request to `/admin/sub-quests/:id`
export const getSubQuestByIdAction = createAsyncThunk(
  'subQuestReport/getSubQuestByIdAction',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/admin/sub-quests/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        data: error.response?.data,
      });
    }
  }
);

// Async action to fetch sub-quest progressions with pagination, sorting, and filtering
// Dynamically constructs the API route based on provided parameters
export const getSubQuestProgressionsAction = createAsyncThunk(
  'subQuestReport/getSubQuestProgressionsAction',
  async (
    {
      id,
      query,
      currentPage,
      pageSize,
      sortBy,
      orderBy,
      userName,
      courseId,
      questId,
      result,
    },
    { rejectWithValue }
  ) => {
    try {
      let apiRoute = `/admin/sub-quests/${id}/progressions?currentPage=${currentPage}&pageSize=${pageSize}`;

      // Add general search query
      if (query) apiRoute += `&query=${query}`;

      // Add sorting parameters
      if (sortBy) apiRoute += `&sortBy=${sortBy}`;
      if (orderBy) apiRoute += `&orderBy=${orderBy}`;

      // Add filter parameters
      if (userName) {
        apiRoute += `&userName=${userName}`;
      }
      if (courseId) {
        apiRoute += `&courseId=${courseId}`;
      }
      if (questId) {
        apiRoute += `&questId=${questId}`;
      }
      if (result) {
        apiRoute += `&result=${result}`;
      }

      const response = await axios.get(apiRoute);
      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        data: error.response?.data,
      });
    }
  }
);

// Async action to fetch logs for a specific sub-quest
// Makes a GET request to `/admin/sub-quests/:id/logs` with default pagination
export const getSubQuestLogsAction = createAsyncThunk(
  'subQuestReport/getSubQuestLogsAction',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/admin/sub-quests/${id}/logs?currentPage=1&pageSize=100`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        data: error.response?.data,
      });
    }
  }
);

// Async action to fetch the count of progressions for a specific sub-quest
// Makes a GET request to `/admin/sub-quests/:id/progressions/count`
export const getSubQuestProgressionsCountAction = createAsyncThunk(
  'subQuestReport/getSubQuestProgressionsCountAction',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/admin/sub-quests/${id}/progressions/count`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        data: error.response?.data,
      });
    }
  }
);

// Async action to fetch sessions for a specific progression of a sub-quest
// Makes a GET request to `/admin/sub-quests/:subQuestId/progressions/:progressionId/sessions`
export const getSubQuestProgressionSessionsAction = createAsyncThunk(
  'subQuestReport/getSubQuestProgressionSessionsAction',
  async ({ subQuestId, progressionId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/admin/sub-quests/${subQuestId}/progressions/${progressionId}/sessions`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        data: error.response?.data,
      });
    }
  }
);

// Async action to fetch a specific session for a sub-quest progression
// Makes a GET request to `/admin/sub-quests/:subQuestId/progressions/:progressionId/sessions/:sessionId`
export const getSubQuestProgressionSessionAction = createAsyncThunk(
  'subQuestReport/getSubQuestProgressionSessionAction',
  async ({ subQuestId, progressionId, sessionId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/admin/sub-quests/${subQuestId}/progressions/${progressionId}/sessions/${sessionId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        data: error.response?.data,
      });
    }
  }
);

// Async action to delete a specific session for a sub-quest progression
// Makes a DELETE request to `/admin/sub-quests/:subQuestId/progressions/:progressionId/sessions/:sessionId`
export const deleteSubQuestProgressionSessionsAction = createAsyncThunk(
  'subQuestReport/deleteSubQuestProgressionSessionsAction',
  async ({ subQuestId, progressionId, sessionId }, { rejectWithValue }) => {
    try {
      await axios.delete(
        `/admin/sub-quests/${subQuestId}/progressions/${progressionId}/sessions/${sessionId}`
      );
      return sessionId;
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        data: error.response?.data,
      });
    }
  }
);

// Async action to update a specific quiz session for a sub-quest progression
// Makes a PUT request to `/admin/sub-quests/:subQuestId/progressions/:progressionId/sessions/:sessionId`
export const updateQuizSessionAction = createAsyncThunk(
  'subQuestReport/updateQuizSessionAction',
  async (
    { subQuestId, progressionId, sessionId, data },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.put(
        `/admin/sub-quests/${subQuestId}/progressions/${progressionId}/sessions/${sessionId}`,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        data: error.response?.data,
      });
    }
  }
);

// Async action to delete a specific progression for a sub-quest
// Makes a DELETE request to `/admin/sub-quests/:subQuestId/progressions/:progressionId`
export const deleteSubQuestProgressionAction = createAsyncThunk(
  'subQuestReport/deleteSubQuestProgressionAction',
  async ({ subQuestId, progressionId }, { rejectWithValue }) => {
    try {
      await axios.delete(
        `/admin/sub-quests/${subQuestId}/progressions/${progressionId}`
      );
      return progressionId;
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        data: error.response?.data,
      });
    }
  }
);

// Async action to fetch bulk file submission records for multiple users with optional filters
// Makes a GET request to `/admin/sub-quests/:subQuestId/file-submissions/bulk`
export const getBulkFileSubmissionsAction = createAsyncThunk(
  'subQuestReport/getBulkFileSubmissionsAction',
  async (
    { subQuestId, userIds, courseId, questId, results },
    { rejectWithValue }
  ) => {
    try {
      const queryString = userIds.map((id) => `userIds=${id}`).join('&');

      // Build full API route with base parameters
      let apiRoute = `/admin/sub-quests/${subQuestId}/file-submissions/bulk?${queryString}`;

      if (courseId) apiRoute += `&courseId=${courseId}`;
      if (questId) apiRoute += `&questId=${questId}`;

      if (results && results.length > 0) {
        results.forEach((result) => {
          apiRoute += `&results=${result}`;
        });
      }

      const response = await axios.get(apiRoute);
      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        data: error.response?.data,
      });
    }
  }
);

// Async action to import bulk file submission session records via CSV
// Makes a POST request to `/admin/sub-quests/:subQuestId/file-submissions/bulk`
export const importBulkFileSubmissionsAction = createAsyncThunk(
  'subQuestReport/importBulkFileSubmissionsAction',
  async ({ subQuestId, records }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `/admin/sub-quests/${subQuestId}/file-submissions/bulk`,
        { records }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        data: error.response?.data,
      });
    }
  }
);

// Async action to fetch all user IDs with progressions that matches the filters for a sub-quest
// Makes a GET request to `/admin/sub-quests/:subQuestId/progressions/user-ids`
export const getProgressionUserIdsAction = createAsyncThunk(
  'subQuestReport/getProgressionUserIdsAction',
  async ({ subQuestId, courseId, questId, results }, { rejectWithValue }) => {
    try {
      let apiRoute = `/admin/sub-quests/${subQuestId}/progressions/user-ids?`;

      if (courseId) apiRoute += `&courseId=${courseId}`;
      if (questId) apiRoute += `&questId=${questId}`;

      if (results && results.length > 0) {
        results.forEach((result) => {
          apiRoute += `&results=${result}`;
        });
      }

      const response = await axios.get(apiRoute);
      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        data: error.response?.data,
      });
    }
  }
);
